// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-RBAC-1 — typed security groups: the wire contract (per-domain module, E-05).

import { apiFetch, parseApiError } from './client'

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await apiFetch(path, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) throw await parseApiError(res)
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export type GroupType = 'user' | 'provider' | 'patient' | 'role'   // `facility` ships with Site of Care
export const GROUP_TYPES: { value: GroupType; label: string; members: string; help: string }[] = [
  { value: 'user', label: 'Users', members: 'user accounts', help: 'Give roles and permissions to a set of user accounts.' },
  { value: 'provider', label: 'Providers', members: 'prescribers (NPI)', help: 'Every account linked to one of these prescribers gets the group’s roles and permissions; the group can also be used as a prescriber scope.' },
  { value: 'patient', label: 'Patients', members: 'patients', help: 'A named set of patients, used as a scope (an account sees only these patients). Not a principal.' },
  { value: 'role', label: 'Roles', members: 'roles', help: 'Everyone holding one of these roles is a member — "all Providers" without listing accounts.' },
]
export const groupTypeLabel = (t: string) => GROUP_TYPES.find(x => x.value === t)?.label ?? t

export interface GroupRow {
  id: string; pharmacy_id: string; group_type: GroupType; name: string; description: string | null
  is_active: boolean; member_count: number; grant_count: number; created_at: string
}
export interface GroupMember { id: string; label: string; sub: string | null; missing?: boolean }
export interface GroupDetail {
  group: GroupRow
  members: GroupMember[]
  grants: { roles: { id: string; name: string }[]; permissions: string[] }
}
export interface MemberCandidate { id: string; label: string; sub: string | null }

export const listGroups = (pharmacyId?: string) =>
  req<{ items: GroupRow[] }>('GET', `/groups${pharmacyId ? `?pharmacy_id=${encodeURIComponent(pharmacyId)}` : ''}`)
export const createGroup = (input: { group_type: GroupType; name: string; description?: string; pharmacy_id?: string }) =>
  req<{ id: string }>('POST', '/groups', input)
export const getGroup = (id: string) => req<GroupDetail>('GET', `/groups/${id}`)
export const updateGroup = (id: string, input: { name?: string; description?: string; is_active?: boolean }) =>
  req<{ ok: boolean }>('PUT', `/groups/${id}`, input)
export const setGroupMembers = (id: string, memberIds: string[]) =>
  req<{ ok: boolean; count: number }>('PUT', `/groups/${id}/members`, { member_ids: memberIds })
export const setGroupGrants = (id: string, input: { role_ids: string[]; permissions: string[] }) =>
  req<{ ok: boolean; count: number; sessions_refreshed: number }>('PUT', `/groups/${id}/grants`, input)
export const searchGroupMembers = (type: GroupType, q: string, pharmacyId?: string) =>
  req<{ items: MemberCandidate[] }>('GET', `/groups/member-search?type=${type}&q=${encodeURIComponent(q)}${pharmacyId ? `&pharmacy_id=${encodeURIComponent(pharmacyId)}` : ''}`)

export interface UserGroups {
  direct: GroupRow[]                                   // user-type groups the account is a member of
  via: { group: GroupRow; via: string }[]              // role / provider groups reached indirectly
  available: GroupRow[]                                // user-type groups the account could be added to
}
export const getUserGroups = (userId: string) => req<UserGroups>('GET', `/users/${userId}/groups`)
export const setUserGroups = (userId: string, groupIds: string[]) =>
  req<{ ok: boolean; count: number }>('PUT', `/users/${userId}/groups`, { group_ids: groupIds })
